/* The receptora screens: fieldsets and field widths, the receptora list (counts, tabs, toolbar, table, pager), row actions and hints, and the pieces every screen shares. */

.receptora-page form,
.receptora-page fieldset {
  display: grid;
  gap: 12px;
  min-width: 0;
}

/* A form's buttons keep their own width instead of stretching across the grid. */
.receptora-page :is(form, fieldset) > button {
  justify-self: start;
}

.receptora-page fieldset {
  margin: 0 0 16px;
  border: 1px solid var(--color-finnest-border);
  border-radius: var(--radius-finnest-control);
  background: var(--color-finnest-surface);
  padding: 14px 16px;
}

.receptora-page fieldset fieldset {
  margin: 0;
  background: transparent;
}

.receptora-page legend {
  padding: 0 4px;
  color: var(--color-finnest-ink);
  font-weight: 750;
}

/* The journey's fields stay at a readable width; their look is the console's field rule. */
.receptora-page :is(input, select) {
  max-width: 420px;
}

.receptora-page textarea {
  max-width: 640px;
}

.receptora-hint {
  margin: 0;
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.receptora-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

/* The receptora list: the four counts, the panel with its underline tabs, the toolbar, the rich table and its pager. */
.receptora-button-icon {
  width: 15px;
  height: 15px;
}

.receptora-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.receptora-tile {
  display: grid;
  gap: 4px;
  border: 1px solid var(--color-finnest-border);
  border-radius: var(--radius-finnest-control);
  background: var(--color-finnest-surface);
  padding: 14px 16px;
  color: var(--color-finnest-ink);
  font: inherit;
  text-align: left;
  transition:
    border-color 0.16s,
    box-shadow 0.16s;
}

button.receptora-tile {
  cursor: pointer;
}

button.receptora-tile:hover {
  border-color: rgb(91 50 230 / 28%);
  box-shadow: var(--shadow-finnest-subtle);
}

button.receptora-tile:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-finnest-focus);
}

.receptora-tile-label,
.receptora-tile-hint {
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.receptora-tile-label {
  font-weight: 650;
}

.receptora-tile-value {
  font-size: 26px;
  font-weight: 750;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.receptora-tile[data-tone='ok'] .receptora-tile-value {
  color: #0f6f74;
}

.receptora-tile[data-tone='warn'] .receptora-tile-value {
  color: #9a3412;
}

.receptora-tile[data-tone='danger'] .receptora-tile-value {
  color: #b42318;
}

.receptora-panel {
  min-width: 0;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-surface);
}

.receptora-panel-body {
  padding: 14px 16px;
}

.receptora-page-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  border-bottom: 1px solid var(--color-finnest-border);
  padding: 0 10px;
}

.receptora-page-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: var(--color-finnest-muted);
  font-weight: 650;
  text-decoration: none;
  white-space: nowrap;
}

.receptora-page-tab:hover {
  color: var(--color-finnest-ink);
}

.receptora-page-tab:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-finnest-focus);
}

.receptora-page-tab[aria-selected='true'] {
  color: var(--color-finnest-accent-strong);
}

.receptora-page-tab[aria-selected='true']::after {
  position: absolute;
  right: 8px;
  bottom: -1px;
  left: 8px;
  height: 2px;
  border-radius: 2px;
  background: var(--color-finnest-accent);
  content: '';
}

.receptora-tab-count {
  border-radius: 999px;
  background: var(--color-finnest-muted-surface);
  padding: 0 7px;
  color: var(--color-finnest-muted);
  font-size: 11.5px;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}

.receptora-page-tab[aria-selected='true'] .receptora-tab-count {
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
}

.receptora-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--color-finnest-border);
  padding: 12px 16px;
}

.receptora-search {
  position: relative;
  flex: 1 1 260px;
  max-width: 380px;
}

.receptora-search svg {
  position: absolute;
  top: 50%;
  left: 10px;
  width: 15px;
  height: 15px;
  transform: translateY(-50%);
  color: var(--color-finnest-muted);
  pointer-events: none;
}

.receptora-page .receptora-search input,
.receptora-page .receptora-select {
  min-height: 34px;
  border-radius: var(--radius-finnest-control);
  background: var(--color-finnest-surface);
}

.receptora-page .receptora-search input {
  width: 100%;
  max-width: none;
  padding: 0 10px 0 32px;
}

/* A long application name never widens the toolbar past a phone screen. */
.receptora-page .receptora-select {
  width: auto;
  min-width: 0;
  max-width: 100%;
  padding: 0 28px 0 10px;
}

.receptora-page .receptora-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  border: 0;
  background: transparent;
  padding: 0;
}

.receptora-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 999px;
  background: var(--color-finnest-surface);
  padding: 3px 10px;
  color: var(--color-finnest-ink);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.receptora-status-chip b {
  color: var(--color-finnest-muted);
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}

.receptora-status-chip[aria-pressed='true'] {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
}

.receptora-status-chip[aria-pressed='true'] b {
  color: var(--color-finnest-accent-strong);
}

.receptora-toolbar-spacer {
  flex: 1;
}

.receptora-linkish {
  border: 0;
  background: none;
  padding: 0;
  color: var(--color-finnest-accent);
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;
}

.receptora-list-table td {
  vertical-align: middle;
}

.receptora-list-table tbody tr {
  cursor: pointer;
  transition: background-color 0.12s;
}

.receptora-list-table tbody tr:hover {
  background: #faf9fe;
}

.receptora-list-table tbody tr:focus-visible {
  outline: 0;
  box-shadow: inset var(--shadow-finnest-focus);
}

.receptora-who {
  display: flex;
  align-items: center;
  gap: 10px;
}

.receptora-who-name {
  display: block;
  color: var(--color-finnest-ink);
  font-weight: 650;
  text-decoration: none;
}

.receptora-who-name:hover {
  color: var(--color-finnest-accent);
}

.receptora-who small {
  color: var(--color-finnest-muted);
}

.receptora-err-line {
  display: block;
  max-width: 280px;
  margin-top: 2px;
  color: #b42318;
  font-size: 12px;
  white-space: normal;
}

.receptora-records {
  display: flex;
  gap: 10px;
}

.receptora-records b {
  color: var(--color-finnest-ink);
  font-weight: 650;
}

.receptora-expiry {
  display: grid;
  gap: 4px;
  min-width: 130px;
}

.receptora-expiry[data-tone='warn'] span {
  color: #9a3412;
  font-weight: 650;
}

.receptora-bar {
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--color-finnest-muted-surface);
}

.receptora-bar i {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--color-finnest-trust);
}

.receptora-expiry[data-tone='warn'] .receptora-bar i {
  background: var(--color-finnest-brand-warm);
}

.receptora-expiry[data-tone='off'] .receptora-bar i {
  background: #c9c9d1;
}

/* A listed connection's actions: ghost buttons on one line, what an action did under them. */
.receptora-row-actions {
  display: grid;
  justify-items: end;
  gap: 4px;
  white-space: normal;
}

.receptora-row-actions .admin-header-actions {
  flex-wrap: nowrap;
  gap: 4px;
}

.receptora-row-actions .fui-button {
  min-height: 28px;
  border-color: transparent;
  background: transparent;
  box-shadow: none;
  padding: 0 9px;
  font-size: 12.5px;
}

.receptora-row-actions .fui-button-secondary {
  color: var(--color-finnest-ink);
}

.receptora-row-actions .fui-button-danger {
  color: #b42318;
}

.receptora-row-actions .fui-button:hover:not(:disabled) {
  background: var(--color-finnest-muted-surface);
}

.receptora-row-actions [role='alert'] {
  max-width: 320px;
  margin: 0;
  color: #b42318;
  font-size: 12px;
}

.receptora-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid var(--color-finnest-border);
  padding: 10px 16px;
  color: var(--color-finnest-muted);
  font-size: 12.5px;
}

.receptora-pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.receptora-pages button {
  min-width: 30px;
  min-height: 28px;
  border: 1px solid var(--color-finnest-border);
  border-radius: var(--radius-finnest-control);
  background: var(--color-finnest-surface);
  color: var(--color-finnest-ink);
  font: inherit;
  font-size: 12.5px;
  cursor: pointer;
}

.receptora-pages button[aria-current='page'] {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
  font-weight: 700;
}

.receptora-empty {
  margin: 0;
  padding: 28px 16px;
  color: var(--color-finnest-muted);
  text-align: center;
}

.receptora-empty strong {
  color: var(--color-finnest-ink);
}

@media (max-width: 860px) {
  .receptora-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Shared pieces of every receptora screen: avatars, pills and status badges of one size, copy chips. */
.receptora-avatar {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
}

.receptora-avatar[data-size='large'] {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
}

.receptora-pills {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

/* A pill and a status badge share one height, so they line up in any row. */
.receptora-pill,
.receptora-page .fui-status-badge {
  box-sizing: border-box;
  height: 22px;
  min-height: 22px;
  line-height: 1;
  white-space: nowrap;
}

.receptora-pill {
  display: inline-flex;
  flex: none;
  align-items: center;
  border: 1px solid rgb(91 50 230 / 14%);
  border-radius: 999px;
  background: var(--color-finnest-accent-soft);
  padding: 0 9px;
  color: var(--color-finnest-accent-strong);
  font-size: 12px;
  font-weight: 650;
}

.receptora-pill[data-more] {
  border-color: var(--color-finnest-border);
  background: var(--color-finnest-muted-surface);
  color: var(--color-finnest-muted);
}

.receptora-copy {
  border: 0;
  border-radius: 4px;
  background: var(--color-finnest-muted-surface);
  padding: 1px 6px;
  color: var(--color-finnest-ink);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  cursor: pointer;
}

.receptora-copy:hover {
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
}

.receptora-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

.receptora-num {
  font-variant-numeric: tabular-nums;
}
